import {
  PricingSummary,
  PricingSummaryRange,
  Phase,
  PricingLevel,
  PricingType,
  PriceRange,
  GpmType,
  FixedGpm,
  RangeGpm,
  isPrice,
  Price,
  isPriceRange,
  isPricingSummary,
  isPricingSummaryRange,
  pricingFieldsByType,
  joinPricing,
  isSinglePrice,
  PricingOptions,
  Work,
} from "../../data-types";
export const sumLegalFeePriceSingle = (
  pricingOptions: PricingSummary,
  phases: Phase[] | null | undefined,
  pricingLevel: PricingLevel,
  pricingType: PricingType,
  matterWork: Work[] | null,
  topDownRateDiscount: number
): number => {
  if (pricingType === PricingType.None) return 0;

  let legalFees = 0;

  if (
    pricingLevel === PricingLevel.Matter &&
    pricingType !== PricingType.EstimatedRange &&
    pricingType !== PricingType.TopDown
  ) {
    legalFees = (pricingOptions?.chosen as number) || 0;
  } else if (pricingLevel === PricingLevel.Phase) {
    legalFees =
      phases?.reduce(
        (acc, p) =>
          acc + ((pricingFieldsByType(pricingType, p.priceOptions!)?.chosen as number) || 0),
        0
      ) ?? 0;
  } else if (pricingLevel === PricingLevel.FeeEarner) {
    legalFees =
      phases
        ?.map((p) => {
          const phasePrice =
            (pricingFieldsByType(pricingType, p.priceOptions!)?.chosen as number) ??
            sumWorkPriceSingle(p.work);
          return phasePrice;
        })
        .reduce((acc, val) => acc + val, 0) ?? 0;
  } else if (pricingLevel === PricingLevel.Matter && pricingType === PricingType.TopDown) {
    legalFees = pricingOptions?.chosen
      ? (pricingOptions?.chosen as number)
      : (1 - (topDownRateDiscount ?? 0) / 100) * sumWorkPriceSingle(matterWork ?? null);
    legalFees = Number(legalFees.toFixed(2));
  }
  return legalFees;
};

export const sumLegalFeePriceRange = (
  pricingSummary: PricingSummaryRange,
  phases: Phase[] | null | undefined,
  pricingLevel: PricingLevel
): { lower: number; upper: number } => {
  let legalFees = { lower: 0, upper: 0 };

  if (pricingSummary == null || pricingSummary?.chosen == null) return legalFees;

  if (pricingLevel === PricingLevel.Matter) {
    legalFees = {
      lower: pricingSummary.chosen?.lower || 0,
      upper: pricingSummary.chosen?.upper || 0,
    };
  } else if (pricingLevel === PricingLevel.Phase) {
    legalFees = phases?.reduce(
      (acc, p) => {
        const tranformed: PriceRange = pricingFieldsByType(
          PricingType.EstimatedRange,
          p.priceOptions!
        ).chosen;

        const lower = tranformed?.lower || 0;
        const upper = tranformed?.upper || 0;
        return {
          lower: acc.lower + lower,
          upper: acc.upper + upper,
        };
      },
      { lower: 0, upper: 0 }
    ) ?? { lower: 0, upper: 0 };
  } else if (pricingLevel === PricingLevel.FeeEarner) {
    legalFees = phases
      ?.map((p) => {
        const chosenPrice = pricingFieldsByType(PricingType.EstimatedRange, p.priceOptions!)
          .chosen as PriceRange;
        const workSum = sumWorkPriceRange(p.work);
        return {
          lower: chosenPrice?.lower ?? workSum.lower,
          upper: chosenPrice?.upper ?? workSum.upper,
        };
      })
      .reduce(
        (acc, val) => ({
          lower: acc.lower + (val.lower || 0),
          upper: acc.upper + (val.upper || 0),
        }),
        { lower: 0, upper: 0 }
      ) ?? { lower: 0, upper: 0 };
  }
  return legalFees;
};
export const sumWorkPriceRange = (work: Work[] | null): { lower: number; upper: number } =>
  work?.reduce(
    (acc, { price: { lower, upper } }) => ({
      lower: acc.lower + (lower || 0),
      upper: acc.upper + (upper || 0),
    }),
    { lower: 0, upper: 0 }
  ) || { lower: 0, upper: 0 };

export const sumWorkPriceSingle = (work: Work[] | null): number =>
  work?.reduce((acc, { price: { single } }) => acc + (single || 0), 0) || 0;

const sumWorkCostSingle = (work: Work[] | null): number =>
  work?.reduce((acc, { cost: { single } }) => acc + (single || 0), 0) || 0;

const sumWorkCostRange = (work: Work[] | null): { lower: number; upper: number } =>
  work?.reduce(
    (acc, { cost: { lower, upper } }) => ({
      lower: acc.lower + (lower || 0),
      upper: acc.upper + (upper || 0),
    }),
    { lower: 0, upper: 0 }
  ) || { lower: 0, upper: 0 };

export const sumMatterCostRange = (
  phases: Phase[] | null | undefined,
  matterWork: Work[] | null,
  pricingLevel: PricingLevel,
  pricingType: PricingType
): { lower: number; upper: number } | null => {
  if (pricingLevel !== PricingLevel.FeeEarner && pricingType !== PricingType.TopDown) {
    return null;
  }

  const matterCost = sumWorkCostRange(matterWork);
  const phaseCost = phases?.reduce(
    (acc, p) => {
      const phaseTotal = sumWorkCostRange(p.work);
      return {
        lower: acc.lower + phaseTotal.lower,
        upper: acc.upper + phaseTotal.upper,
      };
    },
    { lower: 0, upper: 0 }
  ) || { lower: 0, upper: 0 };

  return {
    lower: matterCost.lower + phaseCost.lower,
    upper: matterCost.upper + phaseCost.upper,
  };
};

export const sumMatterCostSingle = (
  phases: Phase[] | null | undefined,
  matterWork: Work[] | null,
  pricingLevel: PricingLevel,
  pricingType: PricingType
): number | null => {
  if (pricingLevel !== PricingLevel.FeeEarner && pricingType !== PricingType.TopDown) {
    return null;
  }

  const matterCost = sumWorkCostSingle(matterWork);
  const phaseCost = phases?.reduce((acc, p) => acc + sumWorkCostSingle(p.work), 0) ?? 0;
  const totalCost = matterCost + phaseCost;
  return totalCost > 0 ? totalCost : null;
};

export const sumMatterWorkPriceSingle = (
  phases: Phase[] | null | undefined,
  matterWork: Work[] | null,
  topDownRateDiscount: number | null | undefined
): number => {
  const matterWorkPrice = (1 - (topDownRateDiscount ?? 0) / 100) * sumWorkPriceSingle(matterWork);
  const phaseWorkPrice =
    phases?.map((phase) => sumWorkPriceSingle(phase.work)).reduce((acc, val) => acc + val, 0) ?? 0;
  return matterWorkPrice + phaseWorkPrice;
};

export const sumMatterWorkPriceRange = (
  phases: Phase[] | null | undefined,
  matterWork: Work[] | null,
  topDownRateDiscount: number | null | undefined
): { lower: number; upper: number } => {
  const matterWorkPrice = sumWorkPriceRange(matterWork);
  const phaseWorkPrice = phases
    ?.map((phase) => sumWorkPriceRange(phase.work))
    .reduce(
      (acc, val) => ({
        lower: acc.lower + val.lower,
        upper: acc.upper + val.upper,
      }),
      { lower: 0, upper: 0 }
    ) || { lower: 0, upper: 0 };
  return {
    lower: (1 - (topDownRateDiscount ?? 0) / 100) * matterWorkPrice.lower + phaseWorkPrice.lower,
    upper: (1 - (topDownRateDiscount ?? 0) / 100) * matterWorkPrice.upper + phaseWorkPrice.upper,
  };
};

export const priceOrPriceRangeOrPricingSummaryOrPricingSummaryRangeIsZero = (
  priceOrPriceRange: Price | PriceRange | PricingSummary | PricingSummaryRange | undefined
): boolean => {
  if (!priceOrPriceRange) {
    return true;
  } else if (isPrice(priceOrPriceRange)) {
    return priceOrPriceRange?.amount === 0;
  } else if (isPriceRange(priceOrPriceRange)) {
    return (
      (priceOrPriceRange as PriceRange)?.lower === 0 &&
      (priceOrPriceRange as PriceRange)?.upper === 0
    );
  } else if (isPricingSummary(priceOrPriceRange)) {
    return priceOrPriceRange?.suggested === 0 || priceOrPriceRange?.suggested === null;
  } else if (isPricingSummaryRange(priceOrPriceRange)) {
    return (
      (priceOrPriceRange?.suggested?.lower === 0 || priceOrPriceRange?.suggested?.lower === null) &&
      (priceOrPriceRange?.suggested?.upper === 0 || priceOrPriceRange?.suggested?.upper === null)
    );
  }
  return true;
};

export const pricingTypeIsZero = (
  pricingType: PricingType,
  pricingOptions: PricingOptions
): boolean => {
  if (pricingType === PricingType.None) {
    return true;
  }

  const pricing = pricingFieldsByType(pricingType, pricingOptions);

  const pricingIsZero = priceOrPriceRangeOrPricingSummaryOrPricingSummaryRangeIsZero(pricing);

  if (pricingType !== PricingType.EstimatedRange) {
    return pricingIsZero;
  }

  const fixedPricingIsZero = priceOrPriceRangeOrPricingSummaryOrPricingSummaryRangeIsZero(pricing);

  return fixedPricingIsZero && pricingIsZero;
};

export const calculateGpm = (
  cost: number | null | undefined,
  price: number | null | undefined
): FixedGpm | null => {
  if (price == null || price === 0 || cost == null) return null;
  return {
    fixed: Math.round(((price - cost) / price) * 100),
    type: GpmType.Fixed,
  };
};

type MixedGpmInput = {
  cost: (number | Price | PriceRange | undefined | null)[] | undefined;
  price: (number | Price | PriceRange | undefined | null)[] | undefined;
};

export const calculateMixedGpm = (args: MixedGpmInput) => {
  const joinedCosts = joinPricing(args.cost);

  const joinedPrices = joinPricing(args.price);

  if (isSinglePrice(joinedCosts) && isSinglePrice(joinedPrices)) {
    const singleGpm = calculateGpm(joinedCosts, joinedPrices);

    return singleGpm;
  }

  if (isPriceRange(joinedCosts) && isPriceRange(joinedPrices)) {
    const rangeGpm = calculateGpmRange(joinedCosts, joinedPrices);

    return rangeGpm;
  }

  if (isSinglePrice(joinedCosts) && isPriceRange(joinedPrices)) {
    const rangeGpm = calculateGpmRange({ lower: joinedCosts, upper: joinedCosts }, joinedPrices);

    return rangeGpm;
  }

  if (isPriceRange(joinedCosts) && isSinglePrice(joinedPrices)) {
    const rangeGpm = calculateGpmRange(joinedCosts, { lower: joinedPrices, upper: joinedPrices });

    return rangeGpm;
  }

  return null;
};

export const calculateGpmRange = (cost: PriceRange, price: PriceRange): RangeGpm | null => {
  const gpmLowerPoint = calculateGpm(cost?.lower, price?.lower);
  const gpmUpperPoint = calculateGpm(cost?.upper, price?.upper);

  if (!gpmLowerPoint || !gpmUpperPoint) {
    return null;
  }

  return {
    lower: gpmLowerPoint?.fixed || 0,
    upper: gpmUpperPoint?.fixed || 0,
    type: GpmType.Range,
  };
};
